'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/lib/store';
import { fetchActiveSettings } from '@/lib/store/slices/settingsSlice';
import { setTheme } from '@/lib/store/slices/uiSlice';
import Image from 'next/image';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { Navigation } from './Navigation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslations } from 'next-intl';

interface HeaderProps {
  locale: string;
}

const navHrefs = [
  { key: 'home',         href: '' },
  { key: 'about',        href: '/about' },
  { key: 'projects',     href: '/projects' },
  { key: 'services',     href: '/services' },
  { key: 'testimonials', href: '/testimonials' },
  { key: 'blog',         href: '/blog' },
  { key: 'tools',        href: '/tools' },
  { key: 'contact',      href: '/contact' },
  // { key: 'feedback',      href: '/feedback' },
] as const;

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');


export default function Header({ locale }: HeaderProps) {
  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const drawerRef    = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations('nav');

  const dispatch = useDispatch<AppDispatch>();
  const settings = useSelector((s: RootState) => s.settings.currentItem);
  const theme    = useSelector((s: RootState) => s.ui.theme);
  const dark     = theme === 'dark';

  const lang     = locale === 'ar' ? 'ar' : 'en';
  const items    = navHrefs.map((item) => ({ label: t(item.key), href: `/${locale}${item.href}` }));
  const siteName = (lang === 'ar' ? settings?.siteName?.ar : settings?.siteName?.en) || 'Mohamed Saber';
  const logoUrl  = settings?.logo?.url || '/logo.png';

  useEffect(() => { dispatch(fetchActiveSettings()); }, [dispatch]);

  /* Scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close drawer on resize ≥ md */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => { if (e.matches) setMobileOpen(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /* Focus management + focus trap + keyboard close */
  useEffect(() => {
    if (!mobileOpen) {
      // Return focus to the hamburger button when drawer closes
      hamburgerRef.current?.focus();
      return;
    }

    // Move focus into the drawer on open
    const drawer = drawerRef.current;
    if (drawer) {
      const first = drawer.querySelectorAll<HTMLElement>(FOCUSABLE)[0];
      first?.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }

      if (e.key !== 'Tab' || !drawer) return;

      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusable.length) return;

      const first = focusable[0];
      const last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  /* Prevent body scroll when drawer open */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const toggleTheme = () => dispatch(setTheme(dark ? 'light' : 'dark'));

  return (
    <>
      <header
        className={[
          'fixed z-50 h-16 transition-all duration-250 border-(--border)',
          scrolled
            ? 'top-4 inset-x-4 border sm:inset-x-6 max-w-5xl sm:max-w-6xl mx-auto rounded-full bg-bg/75 backdrop-blur-md shadow-md'
            : 'top-0 inset-x-0 rounded-none bg-transparent',
        ].join(' ')}
      >
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link
            href={`/${locale}`}
            aria-label={`${siteName} — home`}
            className="flex items-center shrink-0 focus-visible:outline-2 focus-visible:outline-primary-500 rounded-md"
          >
            <Image
              src={logoUrl}
              alt={`${siteName} Logo`}
              width={137}
              height={56}
              sizes="80px"
              className="w-auto h-8"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex flex-1 justify-center">
            <Navigation locale={locale} items={items} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={toggleTheme}
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-(--muted) hover:text-(--text) hover:bg-(--surface) transition-colors duration-180 cursor-pointer"
            >
              {dark ? <Sun className="w-4 h-4" aria-hidden="true" /> : <Moon className="w-4 h-4" aria-hidden="true" />}
            </button>

            <LanguageSwitcher locale={locale} />

            {/* Mobile hamburger */}
            <button
              ref={hamburgerRef}
              onClick={() => setMobileOpen((o) => !o)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-drawer"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-(--text) hover:bg-(--surface) transition-colors duration-180 cursor-pointer"
            >
              {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden animate-fade-in"
        />
      )}

      {/* Mobile drawer — dialog role with aria-modal for proper screen-reader containment */}
      <div
        id="mobile-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={locale === 'ar' ? 'قائمة التنقل' : 'Navigation menu'}
        aria-hidden={!mobileOpen}
        inert={!mobileOpen ? true : undefined}
        className={[
          'fixed top-0 inset-s-0 z-50 h-full w-72 max-w-[85vw]',
          'bg-(--bg) border-e border-(--border) shadow-lg',
          'flex flex-col md:hidden',
          'transition-[transform,visibility] duration-250',
          // Logical properties handle RTL (inset-s-0 = inline-start, rtl:translate-x-full
          // hides off that edge). `invisible` when closed guarantees the off-canvas
          // drawer can never contribute to CLS — Lighthouse ignores layout shifts of
          // visibility:hidden elements — regardless of any transform/RTL edge case.
          // visibility transitions stepwise, so the slide animation is preserved.
          mobileOpen
            ? 'visible translate-x-0'
            : 'invisible -translate-x-full rtl:translate-x-full',
        ].join(' ')}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 h-16 border-b border-(--border) shrink-0">
          <Image
            src={logoUrl}
            alt={`${siteName} Logo`}
            width={137}
            height={56}
            sizes="80px"
            className="w-auto h-8"
          />
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-(--muted) hover:text-(--text) hover:bg-(--surface) transition-colors duration-180 cursor-pointer"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Drawer nav */}
        <div className="flex-1 overflow-y-auto p-4">
          <Navigation locale={locale} items={items} mobile onSelect={() => setMobileOpen(false)} />
        </div>

        {/* Drawer footer */}
        <div className="p-4 border-t border-(--border) flex items-center justify-between">
          <LanguageSwitcher locale={locale} />
          <button
            onClick={toggleTheme}
            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-(--muted) hover:text-(--text) hover:bg-(--surface) transition-colors duration-180 cursor-pointer"
          >
            {dark ? <Sun className="w-4 h-4" aria-hidden="true" /> : <Moon className="w-4 h-4" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </>
  );
}
